const emptyNavigation = () => ({
  categories: []
})

const initialState = () => ({
  cacheKey: '',
  refreshSequence: 0,
  regionList: [],
  followRegionList: [],
  navigation: emptyNavigation(),
  pageNo: 0,
  pageSize: 50,
  totalNo: 0,
  hasMore: true,
  initialized: false,
  loading: false,
  loadingRequestId: '',
  generation: 0
})

function mergeNavigationPage(state, payload) {
  const data = payload.data || {}
  const incomingRegions = data.allRegion || []
  const regionMap = {}
  state.regionList.forEach(region => {
    if (region && region.regionId) regionMap[region.regionId] = region
  })
  incomingRegions.forEach(region => {
    if (!region || !region.regionId) return
    if (regionMap[region.regionId]) Object.assign(regionMap[region.regionId], region)
    else {
      regionMap[region.regionId] = region
      state.regionList.push(region)
    }
  })

  const followMap = {}
  state.followRegionList.forEach(region => {
    if (region && region.regionId) followMap[region.regionId] = true
  })
  ;(data.followRegion || []).forEach(region => {
    if (region && region.regionId && !followMap[region.regionId]) {
      followMap[region.regionId] = true
      state.followRegionList.push(regionMap[region.regionId] || region)
    }
  })

  const navigation = data.navigation || {}
  if ((navigation.categories || []).length > 0)
    state.navigation.categories = navigation.categories.slice()

  state.pageNo = payload.pageNo
  state.pageSize = payload.pageSize
  state.totalNo = payload.totalNo
  state.hasMore = payload.hasMore
  state.initialized = true
}

function firstPageSignature(regionList, navigation, totalNo) {
  return JSON.stringify({
    regions: (regionList || []).map(region => [
      String(region && region.regionId != null ? region.regionId : ''),
      region && region.regionName,
      Number((region && region.followState) || 0),
      (region && region.followTime) || ''
    ]),
    categories: ((navigation && navigation.categories) || []).map(category => [
      String(category && category.code != null ? category.code : ''),
      category && category.name,
      Number((category && category.sortOrder) || 0)
    ]),
    totalNo: Number(totalNo || 0)
  })
}

/**
 * 新版区域导航的进程内轻量分页缓存。
 * 只保存区域索引和分类摘要，不保存设备/场景状态；服务端地址或用户变化时必须重置。
 */
const regionnavigation = {
  state: initialState(),
  mutations: {
    reconcileRegionNavigationSnapshot(state, payload) {
      const incoming = payload.data.allRegion.slice()
      if (payload.hasMore) {
        // A refreshed prefix is not the complete authorization list. Preserve unseen tail
        // entries until a complete snapshot proves their removal; normal paging still dedupes.
        const ids = new Set(incoming.map(region => String(region.regionId)))
        state.regionList.forEach(region => { if (!ids.has(String(region.regionId))) incoming.push(region) })
      }
      state.regionList = incoming
      state.followRegionList = payload.data.followRegion || []
      state.navigation = payload.data.navigation || state.navigation
      state.pageNo = payload.pageNo
      state.pageSize = payload.pageSize
      state.totalNo = payload.totalNo
      state.hasMore = payload.hasMore
      state.initialized = true
    },
    requestRegionContentRefresh(state) {
      state.refreshSequence++
    },
    resetRegionNavigation(state, payload) {
      const next = initialState()
      next.cacheKey = payload && payload.cacheKey ? payload.cacheKey : ''
      next.generation = state.generation + 1
      Object.keys(next).forEach(key => {
        state[key] = next[key]
      })
    },
    setRegionNavigationLoading(state, payload) {
      // loading 必须由实际在途请求持有，旧请求结束时不能清掉新请求的状态。
      // 同时保留 boolean 入参兼容已有调用，新增代码统一使用 requestId 形式。
      if (typeof payload === 'boolean') {
        state.loading = payload
        state.loadingRequestId = ''
        return
      }
      const loading = !!(payload && payload.loading)
      const requestId = payload && payload.requestId ? String(payload.requestId) : ''
      if (loading) {
        state.loading = true
        state.loadingRequestId = requestId
        return
      }
      if (!requestId || requestId === state.loadingRequestId) {
        state.loading = false
        state.loadingRequestId = ''
      }
    },
    mergeRegionNavigationPage(state, payload) {
      mergeNavigationPage(state, payload)
    },
    replaceRegionNavigationPage(state, payload) {
      const next = initialState()
      next.cacheKey = payload.cacheKey || state.cacheKey
      const firstPageSize = Math.max(1, Number(payload.pageSize || state.pageSize || 50))
      const currentSignature = firstPageSignature(
        state.regionList.slice(0, firstPageSize),
        state.navigation,
        state.totalNo
      )
      const nextData = payload.data || {}
      const nextSignature = firstPageSignature(
        nextData.allRegion,
        nextData.navigation,
        payload.totalNo
      )
      if (state.initialized && currentSignature === nextSignature) {
        // 保留已加载的后续页，同时接收新的收藏列表，避免页外区域收藏状态滞留。
        const byId = new Map(state.regionList.map(region => [String(region.regionId), region]))
        ;(nextData.allRegion || []).forEach(region => {
          const cached = byId.get(String(region.regionId))
          if (cached) Object.assign(cached, region)
        })
        state.followRegionList = (nextData.followRegion || []).map(region => {
          const cached = byId.get(String(region.regionId))
          if (cached) Object.assign(cached, region)
          return cached || region
        })
        state.navigation = nextData.navigation || emptyNavigation()
        return
      }
      // 结构相同的进页刷新不提升代次，区域页即可保留卡片和滚动状态；真正变化时才完整重建。
      next.generation =
        state.generation + (state.initialized && currentSignature === nextSignature ? 0 : 1)
      // 原子替换第一页只替换数据，不结束仍在执行的网络请求。
      next.loading = state.loading
      next.loadingRequestId = state.loadingRequestId
      mergeNavigationPage(next, payload)
      Object.keys(next).forEach(key => {
        state[key] = next[key]
      })
    }
  },
  actions: {
    asyReconcileRegionNavigationSnapshot(context, payload) {
      context.commit('reconcileRegionNavigationSnapshot', payload)
    },
    asyRequestRegionContentRefresh(context) {
      context.commit('requestRegionContentRefresh')
    },
    asyResetRegionNavigation(context, payload) {
      context.commit('resetRegionNavigation', payload)
    },
    asySetRegionNavigationLoading(context, loading) {
      context.commit('setRegionNavigationLoading', loading)
    },
    asyMergeRegionNavigationPage(context, payload) {
      context.commit('mergeRegionNavigationPage', payload)
    },
    asyReplaceRegionNavigationPage(context, payload) {
      context.commit('replaceRegionNavigationPage', payload)
    }
  }
}

export default regionnavigation
